import { apiService } from '../../../shared/services/apiServices';
import { AxiosError } from 'axios';
import type { AddUserDetails } from '../types/addUserType';
import type { AdminPrivileges } from '../types/adminPrivilegeTypes';
import { ADMIN_ERROR_MESSAGES } from '../errors';
import { UserRole } from './addUserApiService';

export interface EditAdminResult {
  success: boolean;
  data?: unknown;
  error?: string;
}

export interface EditAdminOptions {
  tenantId?: string;
  privileges?: AdminPrivileges;
}

export const ERROR_MESSAGES = {
  DEFAULT_ERROR: ADMIN_ERROR_MESSAGES.DEFAULT_ERROR,
  NETWORK_ERROR: ADMIN_ERROR_MESSAGES.NETWORK_ERROR,
  UNEXPECTED_ERROR: ADMIN_ERROR_MESSAGES.UNEXPECTED_ERROR,
  EMAIL_ALREADY_EXISTS: ADMIN_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS,
} as const;

/**
 * Maps form role values to backend UserRole enum
 */
const mapUserRole = (role: string): UserRole => {
  const roleMap: Record<string, UserRole> = {
    Administrator: UserRole.ADMIN,
    Admin: UserRole.ADMIN,
    'Super Admin': UserRole.SUPER_ADMIN,
    Client: UserRole.CLIENT,
    'Facility User': UserRole.FACILITY_USER,
    Technician: UserRole.TECHNICIAN,
  };

  // Check if it's already an enum value
  const normalizedRole = role.trim();
  if (Object.values(UserRole).includes(normalizedRole as UserRole)) {
    return normalizedRole as UserRole;
  }

  // Map from form value to enum
  return roleMap[normalizedRole] || UserRole.CLIENT;
};

/**
 * Maps country name to ISO country code
 */
const getCountryCode = (country: string): string => {
  const countryMap: Record<string, string> = {
    Polska: 'PL',
    Poland: 'PL',
  };
  return countryMap[country] || country.toUpperCase().substring(0, 2);
};

/**
 * Formats phone number to include + prefix if not present
 * Assumes Polish numbers (+48) if no country code prefix
 */
const formatPhoneNumber = (phone: string, countryCode: string): string => {
  const cleaned = phone.trim().replace(/\s+/g, '');
  if (cleaned.startsWith('+')) {
    return cleaned;
  }
  // If no + prefix, add country code (default to +48 for PL)
  const code = countryCode === 'PL' ? '+48' : `+${countryCode}`;
  return `${code}${cleaned}`;
};

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  // Handle string errors
  if (typeof errorData === 'string') {
    if (
      errorData.toLowerCase().includes('user with this email already exists')
    ) {
      return ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
    }
    return errorData;
  }

  // Handle object errors - backend returns { message: "..." } or { error: "..." }
  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const errorMessage = apiError.message || apiError.error;

    if (errorMessage) {
      // Check for specific error messages that need translation
      if (
        errorMessage
          .toLowerCase()
          .includes('user with this email already exists')
      ) {
        return ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
      }
      return errorMessage;
    }

    return ERROR_MESSAGES.DEFAULT_ERROR;
  }

  return ERROR_MESSAGES.DEFAULT_ERROR;
};

export const editAdmin = async (
  userId: string,
  userDetails: AddUserDetails,
  options?: EditAdminOptions
): Promise<EditAdminResult> => {
  if (!userId?.trim()) {
    return {
      success: false,
      error: ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  try {
    const countryCode = getCountryCode(userDetails.country.trim());
    const phoneNumber = formatPhoneNumber(
      userDetails.phone.trim(),
      countryCode
    );

    const mappedRole = mapUserRole(userDetails.role);

    const requestBody: {
      name: string;
      email: string;
      role: UserRole;
      tenantId?: string;
      phoneNumber: string;
      countryCode: string;
      city: string;
      state: string;
      zipCode: string;
      addressLine1: string;
      addressLine2: string;
      country: string;
      status?: 'active' | 'inactive';
      privileges?: AdminPrivileges;
    } = {
      name: userDetails.name.trim(),
      email: userDetails.email.trim().toLowerCase(),
      role: mappedRole,
      phoneNumber,
      countryCode,
      city: userDetails.city.trim(),
      state: userDetails.state.trim(),
      zipCode: userDetails.zip.trim(),
      addressLine1: userDetails.address1.trim(),
      addressLine2: userDetails.address2.trim(),
      country: userDetails.country.trim(),
    };

    if (userDetails.status) {
      requestBody.status = userDetails.status;
    }

    if (options?.tenantId) {
      requestBody.tenantId = options.tenantId.trim();
    }

    if (options?.privileges) {
      requestBody.privileges = options.privileges;
    }

    const result = await apiService.patchAPI(
      `loopay-user-management/users/${userId.trim()}`,
      requestBody,
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      return {
        success: true,
        data: result.data,
      };
    } else {
      const errorData = result.data;
      const isNetworkError = result.isNetworkError;

      const errorMessage = extractErrorMessage(errorData);

      // Handle network errors
      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  } catch (error) {
    // Handle unexpected errors
    if (error instanceof AxiosError) {
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      // Handle network errors
      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      // Extract error message from backend response
      const errorMessage = extractErrorMessage(errorData);

      return {
        success: false,
        error: errorMessage,
      };
    }

    // Handle non-Axios errors
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
